<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useQuery, keepPreviousData } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { Roles } from '@/constants.ts'
import smsHttpService, { type SmsMessageRow } from '@/services/http/smsHttpService.ts'
import SmsDetailDialog from '@/components/SmsDetailDialog.vue'
import PhoneContact from '@/pages/registrations/PhoneContact.vue'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.Manager],
  },
})

const { t } = useI18n()

// --- Фильтры ---

const messageTypeCode = ref<string | null>(null)
const deliveryStatusCode = ref<string | null>(null)
// OTP показываем по умолчанию (общий журнал всех SMS); тоггл позволяет скрыть.
const includeOtp = ref(true)
const phone = ref('')
const dateRange = ref<Date[] | null>(null)

// Дебаунс поиска по телефону — не дёргать API на каждый символ.
const phoneDebounced = ref('')
let phoneTimer: ReturnType<typeof setTimeout> | undefined
watch(phone, (value) => {
  clearTimeout(phoneTimer)
  phoneTimer = setTimeout(() => {
    phoneDebounced.value = value
  }, 300)
})

// Фильтр по назначению SMS (purpose): приглашение или код подтверждения. Вид приглашения
// (обычное/командное) — отдельное измерение, показывается в строке (invitationTypeName).
const typeOptions = computed(() => [
  { label: t('sms.types.Invitation'), value: 'Invitation' },
  { label: t('sms.types.OtpCode'), value: 'OtpCode' },
])

// Статусы доставки для фильтра — подписи из справочника БД (без дублирования в i18n),
// полный набор Mobizon (промежуточные + финальные) в порядке справочника.
const deliveryStatusesQuery = useQuery({
  queryKey: ['admin', 'sms', 'delivery-statuses'],
  queryFn: () => smsHttpService.deliveryStatuses(),
  staleTime: Infinity,
})

const deliveryStatusOptions = computed(() =>
  (deliveryStatusesQuery.data.value ?? []).map((s) => ({ label: s.name ?? s.code, value: s.code })),
)

// --- Пагинация ---

const page = ref(1)
const pageSize = ref(50)
const first = computed(() => (page.value - 1) * pageSize.value)

interface PageEvent {
  page: number
  rows: number
}

function onPage(event: PageEvent) {
  page.value = event.page + 1
  pageSize.value = event.rows
}

function toIsoDate(d: Date | undefined | null): string | undefined {
  if (!d) return undefined
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString()
}

// Смена любого фильтра — на первую страницу.
watch([messageTypeCode, deliveryStatusCode, includeOtp, phoneDebounced, dateRange], () => {
  page.value = 1
})

const listQuery = useQuery({
  queryKey: computed(() => [
    'admin',
    'sms',
    'list',
    page.value,
    pageSize.value,
    messageTypeCode.value,
    deliveryStatusCode.value,
    includeOtp.value,
    phoneDebounced.value,
    dateRange.value,
  ]),
  queryFn: () =>
    smsHttpService.list({
      page: page.value,
      pageSize: pageSize.value,
      messageTypeCode: messageTypeCode.value,
      deliveryStatusCode: deliveryStatusCode.value,
      includeOtp: includeOtp.value,
      phone: phoneDebounced.value.trim() || undefined,
      from: toIsoDate(dateRange.value?.[0]),
      to: toIsoDate(dateRange.value?.[1]),
    }),
  placeholderData: keepPreviousData,
})

const rows = computed<SmsMessageRow[]>(() => listQuery.data.value?.items ?? [])
const total = computed(() => listQuery.data.value?.total ?? 0)

// Карточка SMS — в попапе; открывается только по клику на messageId-ссылку.
const selectedId = ref<string | null>(null)
const detailVisible = ref(false)
function openMessage(id: string) {
  selectedId.value = id
  detailVisible.value = true
}

function formatDateTime(value: string | null): string {
  if (value == null) return '—'
  const date = new Date(value)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

type TagSeverity = 'success' | 'warn' | 'danger' | 'secondary' | 'info'

// Цвет статуса отправки: Sent — синий, Failed — красный, иначе серый.
function sendSeverity(code: string | null): TagSeverity {
  if (code === 'Sent') return 'info'
  if (code === 'Failed') return 'danger'
  return 'secondary'
}

// Цвет статуса доставки (коды Mobizon → справочник): Delivered — зелёный; Accepted — синий;
// PartiallyDelivered/Expired — янтарный; Undelivered/Rejected/Deleted — красный; иначе серый.
function deliverySeverity(code: string | null): TagSeverity {
  switch (code) {
    case 'Delivered':
      return 'success'
    case 'Accepted':
      return 'info'
    case 'PartiallyDelivered':
    case 'Expired':
      return 'warn'
    case 'Undelivered':
    case 'Rejected':
    case 'Deleted':
      return 'danger'
    default:
      return 'secondary'
  }
}

function typeLabel(row: SmsMessageRow): string {
  const key = `sms.types.${row.messageTypeCode}`
  const translated = t(key)
  const base = translated === key ? (row.messageTypeName ?? row.messageTypeCode) : translated
  // Для инвайт-SMS уточняем вид приглашения (обычное/командное).
  return row.invitationTypeName ? `${base} · ${row.invitationTypeName}` : base
}
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <h1 class="text-xl font-bold">{{ t('sms.title') }}</h1>

    <!-- Фильтры -->
    <div class="flex flex-wrap items-end gap-3">
      <div class="flex flex-col gap-1">
        <label for="smsType" class="text-sm">{{ t('sms.filters.messageType') }}</label>
        <Select
          id="smsType"
          v-model="messageTypeCode"
          class="w-56"
          :options="typeOptions"
          option-label="label"
          option-value="value"
          show-clear
          :placeholder="t('sms.filters.all')"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label for="smsDelivery" class="text-sm">{{ t('sms.filters.deliveryStatus') }}</label>
        <Select
          id="smsDelivery"
          v-model="deliveryStatusCode"
          class="w-56"
          :options="deliveryStatusOptions"
          option-label="label"
          option-value="value"
          show-clear
          :placeholder="t('sms.filters.all')"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label for="smsPhone" class="text-sm">{{ t('sms.filters.phone') }}</label>
        <InputText
          id="smsPhone"
          v-model="phone"
          class="w-48"
          :placeholder="t('sms.filters.phonePlaceholder')"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label for="smsDates" class="text-sm">{{ t('sms.filters.dateRange') }}</label>
        <DatePicker
          id="smsDates"
          v-model="dateRange"
          class="w-64"
          selection-mode="range"
          :manual-input="false"
          show-button-bar
          date-format="dd.mm.yy"
          :placeholder="t('sms.filters.dateRangePlaceholder')"
        />
      </div>
      <div class="flex items-center gap-2 pb-2">
        <ToggleSwitch input-id="includeOtp" v-model="includeOtp" />
        <label for="includeOtp" class="text-sm">{{ t('sms.filters.includeOtp') }}</label>
      </div>
    </div>

    <DataTable
      :value="rows"
      lazy
      paginator
      :rows="pageSize"
      :first="first"
      :total-records="total"
      :rows-per-page-options="[20, 50, 100]"
      :loading="listQuery.isLoading.value"
      data-key="id"
      class="w-full"
      @page="onPage"
    >
      <template #empty>
        <div class="text-sm text-surface-500 py-4">{{ t('sms.empty') }}</div>
      </template>

      <Column :header="t('sms.columns.messageId')">
        <template #body="{ data }">
          <!-- Переход в карточку — только по клику на messageId. -->
          <button
            type="button"
            class="text-sm text-primary text-left bg-transparent border-0 p-0 cursor-pointer break-all"
            @click="openMessage(data.id)"
          >
            {{ data.providerMessageId ?? '—' }}
          </button>
        </template>
      </Column>
      <Column :header="t('sms.columns.type')">
        <template #body="{ data }">{{ typeLabel(data) }}</template>
      </Column>
      <Column :header="t('sms.columns.phone')">
        <template #body="{ data }"><PhoneContact :phone="data.phone" /></template>
      </Column>
      <Column :header="t('sms.columns.sendStatus')">
        <template #body="{ data }">
          <Tag
            v-if="data.sendStatusName"
            :value="data.sendStatusName"
            :severity="sendSeverity(data.sendStatusCode)"
          />
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>
      <Column :header="t('sms.columns.deliveryStatus')">
        <template #body="{ data }">
          <Tag
            v-if="data.deliveryStatusName"
            :value="data.deliveryStatusName"
            :severity="deliverySeverity(data.deliveryStatusCode)"
          />
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>
      <Column :header="t('sms.columns.createdAt')">
        <template #body="{ data }">
          <span class="text-sm">{{ formatDateTime(data.createdAt) }}</span>
        </template>
      </Column>
      <Column :header="t('sms.columns.sentAt')">
        <template #body="{ data }">
          <span v-if="data.sentAt" class="text-sm">{{ formatDateTime(data.sentAt) }}</span>
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>
      <Column :header="t('sms.columns.deliveredAt')">
        <template #body="{ data }">
          <span v-if="data.deliveredAt" class="text-sm">{{
            formatDateTime(data.deliveredAt)
          }}</span>
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>
    </DataTable>

    <SmsDetailDialog v-model:visible="detailVisible" :id="selectedId" />
  </section>
</template>
